import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { FeedEventRow } from "@/features/feed/FeedEventRow";
import type { KaiFeedSnapshot } from "./kaiFeedTypes";

/**
 * Renders a deterministic `feed_snapshot` artifact inline in Kai chat.
 * Each event row reuses `FeedEventRow` so locode / carrier-logo preferences
 * and inline insight charts match the main Feed page.
 */
export function KaiFeedSnapshot({ spec }: { spec: KaiFeedSnapshot }) {
  const title = spec.title?.trim() || spec.scope.label;

  return (
    <div className="glass-panel my-2 flex min-w-0 flex-col gap-3 overflow-hidden rounded-xl p-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-1 text-base font-semibold text-foreground">
          {title}
        </p>
        <Badge variant="info" className="shrink-0 px-2 py-0.5 text-[10px]">
          {spec.scope.label}
        </Badge>
      </div>

      {spec.events.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No events in this feed slice right now.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {spec.events.map((event, index) => (
            <li key={event.id}>
              <FeedEventRow event={event} index={index} />
            </li>
          ))}
        </ul>
      )}

      {spec.footnotes && spec.footnotes.length > 0 && (
        <ul className="space-y-1 text-xs text-muted-foreground">
          {spec.footnotes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}

      <div className="flex justify-end border-t border-border/50 pt-2">
        <Link
          to={spec.open_in_feed_url}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          Open in Feed
          <ArrowUpRight className="size-3.5" />
        </Link>
      </div>
    </div>
  );
}
